import {
    dossiersAssignment,
    getDossierById,
    getDossiers,
    getEstablishmentByIds,
    getInstructorList,
    getReferenciels,
    getRegionList,
    getUsers,
} from "../../services";
import { _downloadCertificate, cloneObject, groupBy } from "../../helpers";
import {
    AuthUser,
    CommitementDetails,
    CommitmentRequirementDetails,
    Document,
    Dossier,
    DossierDetailType,
    Establishment,
    EstablishmentCategory,
    EstablishmentDbType,
    RolesEnum,
    Session,
} from "../../types";
import { useCallback, useContext, useMemo, useState } from "react";
import {
    getDossierStatusOfDossier,
    getDossierStatusesIdsByUserRole,
} from "./utils";
import { useFetch, useLocalStorage } from "../../hooks";
import {
    dossierStorageKey,
    establishementKey,
    sessionStorageKey,
} from "../../consts";
import { computeLinearProgressValue } from "./Dossier/utils";
import { fetchEstbalishment } from "../Profil/Client/Common/services";
import { InitDataContext } from "../../contexts";

export function useDossier() {
    const [dossierDetails, setDossierDetails] =
        useState<DossierDetailType | null>(null);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const [linearProgressValue, setLinearProgressValue] = useState<number>(0);

    const [establishment, setEstablishement] = useState<Establishment | null>(
        null
    );

    const [currentDossiers, setCurrentDossiers] = useState<Dossier[]>([]);
    const [referenciels, setReferentiels] = useState([]);
    const [establishements, setEstablishments] = useState<Establishment[]>([]);
    const [instructorList, setInstructorList] = useState<any>([]);

    const [assingedValidatorUsers, setAssingedValidatorUsers] = useState<
        AuthUser[]
    >([]);
    const [assignedValidatorUser, setAssignedValidatorUser] =
        useState<AuthUser | null>(null);
    const [regionList, setRegionList] = useState<EstablishmentCategory[]>([]);

    const { fetcher } = useFetch();

    const {
        auth: { user: currentUser ,  currentUserRoleId: userRoleId  },
        [dossierStorageKey]: { dossierStatus },
    } = session;

    const { ...dataInit } = useContext(InitDataContext);

   

    const putDocsOnCommitementRequirements = (
        commitements: CommitementDetails[],
        docs: Document[],
        establishement: Establishment | null
    ): CommitementDetails[] => {
        const docsGroupedByComReqIds = groupBy(
            docs,
            (doc: Document) => doc.commitmentRequirement
        );

        const commitementsEnriched = [...commitements]
            .map((c: CommitementDetails) => {
                if (c.commitmentRequirements.length) {
                    c.commitmentRequirements = [...c.commitmentRequirements]
                        .filter(
                            (comReq: CommitmentRequirementDetails) =>
                                establishement &&
                                Number(comReq.establishmentType) ===
                                    Number(establishement?.establishmentType)
                        )
                        .map((comReq: CommitmentRequirementDetails) => {
                            comReq.documents = docsGroupedByComReqIds[comReq.id]
                                ? docsGroupedByComReqIds[comReq.id]
                                : [];
                            return comReq;
                        });
                }

                return c;
            })
            .filter((c: CommitementDetails) => c.commitmentRequirements.length);

        return commitementsEnriched;
    };

    const enrichDossierDetail = useCallback(async (dossierRes: any) => {
        if (!dossierRes.detail) {
            const referencielDossierRes = await getReferenciels([
                dossierRes.referenciel,
            ]);

            if (!referencielDossierRes.detail) {
                // fetch establishement

                const fetchingEstablishementRes = await fetchEstbalishment(
                    dossierRes.establishment
                );

                if (
                    !fetchingEstablishementRes ||
                    fetchingEstablishementRes.detail
                ) {
                    throw new Error("Fatal Error : establishment not exist !");
                }

                // enrich with referenciel
                const referenciel = {
                    ...referencielDossierRes[0],
                    commitments: putDocsOnCommitementRequirements(
                        referencielDossierRes[0].commitments,
                        dossierRes.documents,
                        fetchingEstablishementRes
                    ),
                };

                delete dossierRes.documents;
                // enrich with dossierStatus
                if (!dossierRes.dossierStatus.id) {
                    dossierRes.dossierStatus = getDossierStatusOfDossier(
                        dossierRes,
                        dossierStatus
                    );
                }

                if (userRoleId === RolesEnum.SYNERPA) {
                    if (dossierRes?.assignedValidatorUser) {
                        const assignedValidatorUserRes = await getUsers([
                            dossierRes?.assignedValidatorUser,
                        ]);

                        if (!assignedValidatorUserRes.detail) {
                            setAssignedValidatorUser(
                                assignedValidatorUserRes[0]
                            );
                        }
                    }
                }

                const linearProgressValueCalculated = [
                    RolesEnum.CLIENT,
                    RolesEnum.ADMIN_REGION,
                    RolesEnum.ADMIN_RESEAU,
                ].includes(userRoleId)
                    ? dossierRes.commitmentsCompleted
                    : computeLinearProgressValue(
                          userRoleId,
                          referenciel.commitments || []
                      ) || 0;
                // je suppose que toujours il y a une établissement sinon ça ne va pas
                setEstablishement(fetchingEstablishementRes);
                setDossierDetails({ ...dossierRes, referenciel });
                setLinearProgressValue(linearProgressValueCalculated);
            }
        }
    }, []);

    const fetchDossierDetail = useCallback(async (dossierId: number) => {
        const dossierRes = await getDossierById(dossierId);
        if (!dossierRes.detail) {
            await enrichDossierDetail(dossierRes);
            if (userRoleId === RolesEnum.ADMIN) {
                await fetchInstructorList();
            }
        }
    }, []);

    const compareDossierStatus = (a: Dossier, b: Dossier) => {
        if (
            a.dossierStatus === b.dossierStatus ||
            null === a.dossierStatus ||
            null === b.dossierStatus
        )
            return 0;
        return a.dossierStatus - b.dossierStatus;
    };
    const _fetchCurrentDossiers = useCallback(async () => {
        if (!currentUser.id) {
            return;
        }
        const establishmentGroup = currentUser.establishmentGroup;
        const region = currentUser.region;

        let dossiersRes = await getDossiers(currentUser.id, {
            userRoleId,
            dossiersStatuses: getDossierStatusesIdsByUserRole(
                userRoleId,
                dossierStatus
            ),
            establishmentGroup,
            region,
        });

        if (!dossiersRes.detail) {
            // Afficher les dossiers en cours de complétude même si les dossiers n'ont pas été soumis email de 22/04/2024 15:22 <raphael.faucher@socotec.com>

            // if (RolesEnum.ADMIN === userRoleId) {
            //     dossiersRes = dossiersRes.filter(
            //         (d: Dossier) => d.submittedAt !== null
            //     );
            // }
            const refesIds = dossiersRes
                .filter((d: Dossier) => d.referenciel)
                .map((d: Dossier) => d.referenciel)
                .filter(
                    (lb: number, index: number, self: number[]) =>
                        self.findIndex((o) => o === lb) === index
                );

            const refsRes = await getReferenciels([...refesIds.map(Number)]);

            if (!refsRes.detail) {
                const establishementIds = dossiersRes
                    .map((d: Dossier) => d.establishment)
                    .filter((establishementId: number) => establishementId);

                const establishements = await getEstablishmentByIds(
                    establishementIds
                );

                if (!establishements.detail) {
                    setReferentiels([...(refsRes as never[])]);
                    setEstablishments([...establishements]);
                    setCurrentDossiers([...dossiersRes]);
                    if (RolesEnum.SYNERPA === userRoleId) {
                        const validatorUserIds = dossiersRes
                            .map((d: Dossier) => d.assignedValidatorUser)
                            .filter((u: any) => u !== null);

                        if (validatorUserIds.length) {
                            const validatorUsers = await getUsers(
                                validatorUserIds
                            );

                            setAssingedValidatorUsers(validatorUsers);
                        }
                    }
                }
            }
        }
    }, []);

    const fetchInstructorList = useCallback(async () => {
        if (RolesEnum.ADMIN === userRoleId) {
            const instructorListRes = await getInstructorList();
            if (!instructorListRes.detail) {
                setInstructorList(instructorListRes);
            }
        }
    }, []);

    const toBulkAssignedDossier = useCallback(
        async (dossierIdsToAssign: number[], instructeurId: number) => {
            fetcher(
                (async () => {
                    const assignmentDossierRes = await dossiersAssignment(
                        dossierIdsToAssign,
                        instructeurId
                    );
                    if (!assignmentDossierRes.detail) {
                        await fetchInstructorList();
                        await _fetchCurrentDossiers();
                        return assignmentDossierRes;
                    }
                })()
            );
        },
        []
    );

    const downloadCertificate = useCallback(async (dossierId: number) => {
        await _downloadCertificate(dossierId, "");
    }, []);

    const referencielsGroupedById = referenciels.length
        ? cloneObject(groupBy(referenciels, (ref: any) => ref.id))
        : null;
    const establishementsGroupedBy = useMemo(
        () =>
            groupBy(
                establishements,
                (establishment: EstablishmentDbType) => establishment.id
            ),
        [establishements]
    );

    const validatorUsersGroupedById = assingedValidatorUsers.length
        ? groupBy(assingedValidatorUsers, (vUser: AuthUser) => vUser.id)
        : null;

    const currentDossiersEnriched = useMemo(() => {
        return currentDossiers.length
            ? currentDossiers.map((dossier: any) => {
                  if (!dossier.establishment.id) {
                      dossier.establishment =
                          establishementsGroupedBy[dossier.establishment][0];
                  }

                  if (!dossier.referenciel.id) {
                      const refFromDb = cloneObject(
                          referencielsGroupedById[dossier.referenciel][0]
                      );

                      const referenciel = {
                          ...refFromDb,
                          commitments: putDocsOnCommitementRequirements(
                              [...refFromDb.commitments],
                              dossier.documents,
                              dossier.establishment
                          ),
                      };

                      dossier.referenciel = referenciel;
                      delete dossier.documents;
                  }
                  if (!dossier.dossierStatus.id) {
                      dossier.dossierStatus = getDossierStatusOfDossier(
                          dossier,
                          dossierStatus
                      );
                  }
                  if (userRoleId === RolesEnum.SYNERPA) {
                      if (
                          dossier?.assignedValidatorUser &&
                          !dossier?.assignedValidatorUser.id &&
                          validatorUsersGroupedById
                      ) {
                          dossier.assignedValidatorUser =
                              validatorUsersGroupedById[
                                  dossier.assignedValidatorUser
                              ][0];
                      }
                  }

                  return dossier;
              })
            : [];
    }, [referencielsGroupedById, instructorList]);

    const getEstablishmentCategoryFromStorage = () => {
        const establishmentGroupsFromContext =
            dataInit[establishementKey]?.establishmentGroups || [];
        const establishmentTypesFromContext =
            dataInit[establishementKey]?.establishmentTypes || [];

        if (
            establishmentGroupsFromContext.length &&
            establishmentTypesFromContext.length
        ) {
            return {
                establishmentGroups: establishmentGroupsFromContext,
                establishmentTypes: establishmentTypesFromContext,
            };
        }

        if (session && session[establishementKey]) {
            const {
                [establishementKey]: {
                    establishmentGroups: storedEstablishmentGroups,
                    establishmentTypes: storedEstablishmentTypes,
                },
            } = session || {};
            return {
                establishmentGroups: storedEstablishmentGroups,
                establishmentTypes: storedEstablishmentTypes,
            };
        }
        return {
            establishmentGroups: [],
            establishmentTypes: [],
        };
    };

    const { establishmentGroups } = getEstablishmentCategoryFromStorage();

    const fetchRegionList = useCallback(async () => {
        const regionList = await getRegionList();
        if (!regionList.detail) {
            setRegionList([...regionList]);
        }
    }, []);

    return {
        putDocsOnCommitementRequirements,
        fetchDossierDetail,
        dossierDetails,
        setDossierDetails,
        linearProgressValue,
        establishment,
        // fetchCurrentDossiers,
        fetchInstructorList,
        instructorList,
        toBulkAssignedDossier,
        compareDossierStatus,
        _fetchCurrentDossiers,
        currentDossiers: currentDossiersEnriched,
        establishements,
        assignedValidatorUser,
        downloadCertificate,
        establishmentGroups,
        fetchRegionList,
        regionList,
    };
}
